import { Avatar } from '@/src/tailwindui/avatar';
import {
    DropdownButton,
    DropdownDivider,
    DropdownLabel,
    DropdownMenu,
    DropdownItem,
    Dropdown,
} from '@/src/tailwindui/dropdown';
import {
    Sidebar,
    SidebarBody,
    SidebarFooter,
    SidebarHeader,
    SidebarItem,
    SidebarLabel,
    SidebarSection,
} from '@/src/tailwindui/sidebar';
import { SidebarLayout } from '@/src/tailwindui/sidebar-layout';
import { Navbar, NavbarSection, NavbarSpacer } from '@/src/tailwindui/navbar';
import { NavbarItem } from '@/src/tailwindui/navbar';
import logo from '../../pe-logo.svg';

import {
    ArrowRightStartOnRectangleIcon,
    ChartBarIcon,
    CheckIcon,
    ChevronDownIcon,
    ChevronUpIcon,
    Cog8ToothIcon,
    MapIcon,
    MegaphoneIcon,
    PlusIcon,
    UserCircleIcon,
} from '@heroicons/react/24/outline';
import {
    Cog6ToothIcon,
    QuestionMarkCircleIcon,
    Square2StackIcon,
    DeviceTabletIcon,
    DevicePhoneMobileIcon,
} from '@heroicons/react/24/outline';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useContext, useEffect } from 'react';
import AuthContext from '@/src/Authentication/components/AuthContext';
import { useLocalStorage } from './hooks/useLocalStorage';
import useFeatureFlags from '@/src/Features/components/hooks/useFeatureFlags';
import useLogout from '@/src/Authentication/components/hooks/useLogout';
import OrgContext from '@/src/Authentication/components/OrgContext';
import FeatureFlagContext from '@/src/Features/components/FeatureFlagContext';
import { Toaster } from 'sonner';
import useAuth from '@/src/Authentication/components/hooks/useAuth';
import useCurrentLicense from '@/src/Authentication/components/hooks/useCurrentLicense';
import LicenseContext from '@/src/Authentication/components/LicenseContext';
import FeatureFlag from '@/src/Features/components/FeatureFlag';
import { FeatureFlags } from '@/src/Features/lib/constants';
import useUserOrg from '@/src/Authentication/components/hooks/useUserOrg';

function AccountDropdownMenu({
    anchor,
}: {
    anchor: 'top start' | 'bottom end';
}) {
    const logout = useLogout();
    const {
        auth: { orgs },
    } = useContext(AuthContext);
    const [, setOrg] = useLocalStorage('current-org', orgs?.[0]);
    const navigate = useNavigate();
    return (
        <DropdownMenu className="min-w-64 dark-dropdown-menu" anchor={anchor}>
            <DropdownItem href="#" className="dark-dropdown-menu-item">
                <UserCircleIcon />
                <DropdownLabel
                    className="text-lg"
                    onClick={() => {
                        navigate('/account');
                    }}
                >
                    My account
                </DropdownLabel>
            </DropdownItem>
            <DropdownDivider className="dark:border-black/10" />

            <DropdownItem
                onClick={() => {
                    logout();
                    setOrg(null);
                }}
                className="dark-dropdown-menu-item"
            >
                <ArrowRightStartOnRectangleIcon />
                <DropdownLabel className="text-lg">Sign out</DropdownLabel>
            </DropdownItem>
        </DropdownMenu>
    );
}

function ApplicationLayoutComponent({ auth }: { auth: any }) {
    let pathname = useLocation().pathname;
    const navigate = useNavigate();
    const { license } = useCurrentLicense();

    const { featureFlags } = useFeatureFlags();
    const isGlobalAdmin = auth?.roles?.includes(69);
    const { org, setOrg } = useUserOrg();
    const { orgs } = auth;

    return (
        <FeatureFlagContext.Provider value={featureFlags}>
            <LicenseContext.Provider value={{ license }}>
                <SidebarLayout
                    navbar={
                        <Navbar>
                            <NavbarSpacer />
                            <NavbarSection>
                                <Dropdown>
                                    <DropdownButton as={NavbarItem}>
                                        <Avatar src="/users/erica.jpg" square />
                                    </DropdownButton>
                                    <AccountDropdownMenu anchor="bottom end" />
                                </Dropdown>
                            </NavbarSection>
                        </Navbar>
                    }
                    sidebar={
                        <Sidebar>
                            <SidebarHeader>
                                <Dropdown>
                                    <div
                                        onClick={() => {
                                            navigate('/');
                                        }}
                                    >
                                        <img
                                            className="h-[52px] clickable cursor-pointer object-contain pe-logo mb-[24px]"
                                            src={logo}
                                            alt="PhotoEngineers"
                                        />
                                    </div>
                                    {orgs?.length > 1 && (
                                        <DropdownButton as={SidebarItem}>
                                            <Avatar src="/teams/catalyst.svg" />
                                            <SidebarLabel>
                                                {org?.name}
                                            </SidebarLabel>
                                            <ChevronDownIcon className="ml-2" />
                                        </DropdownButton>
                                    )}
                                    <DropdownMenu
                                        className="min-w-80 lg:min-w-64"
                                        anchor="bottom start"
                                    >
                                        <DropdownItem href="#">
                                            <DropdownLabel>
                                                Switch Organization
                                            </DropdownLabel>
                                        </DropdownItem>
                                        <DropdownDivider />
                                        {orgs?.map((o: any) => (
                                            <DropdownItem
                                                href="#"
                                                onClick={() => {
                                                    setOrg(o);
                                                }}
                                            >
                                                <Avatar
                                                    slot="icon"
                                                    src={o?.logo}
                                                />
                                                <DropdownLabel>
                                                    {o?.name}
                                                </DropdownLabel>
                                            </DropdownItem>
                                        ))}
                                    </DropdownMenu>
                                </Dropdown>
                            </SidebarHeader>

                            <SidebarBody>
                                <SidebarSection>
                                    <SidebarItem
                                        current={pathname.startsWith('/events')}
                                        onClick={() => {
                                            navigate('/events');
                                        }}
                                    >
                                        <Square2StackIcon />
                                        <SidebarLabel>Events</SidebarLabel>
                                    </SidebarItem>
                                </SidebarSection>

                                {/*<SidebarSection className="max-lg:hidden">
                            <SidebarHeading>Upcoming Events</SidebarHeading>
                            {events.map((event) => (
                                <SidebarItem key={event.id} href={event.url}>
                                    {event.name}
                                </SidebarItem>
                            ))}
                        </SidebarSection>*/}
                                <SidebarSection>
                                    <p className="text-lg px-2 plex-sans-bold text-black dark:text-white ">
                                        Product
                                    </p>
                                    <SidebarItem
                                        current={
                                            pathname.startsWith('/devices') ||
                                            pathname.startsWith('/registration')
                                        }
                                        onClick={() => {
                                            navigate('/devices');
                                        }}
                                    >
                                        <DeviceTabletIcon />
                                        <SidebarLabel>Devices</SidebarLabel>
                                    </SidebarItem>
                                    <FeatureFlag
                                        flag={FeatureFlags.PE_ORG_SETTINGS}
                                    >
                                        <SidebarItem
                                            current={pathname.startsWith(
                                                '/settings',
                                            )}
                                            onClick={() => {
                                                navigate('/settings');
                                            }}
                                        >
                                            <Cog6ToothIcon />
                                            <SidebarLabel>
                                                Settings
                                            </SidebarLabel>
                                        </SidebarItem>
                                    </FeatureFlag>
                                    <SidebarItem
                                        current={pathname.startsWith('/plans')}
                                        onClick={() => {
                                            navigate('/plans');
                                        }}
                                    >
                                        <CheckIcon />
                                        <SidebarLabel>Plans</SidebarLabel>
                                    </SidebarItem>
                                </SidebarSection>
                                {isGlobalAdmin && (
                                    <SidebarSection>
                                        <p className="text-lg px-2 plex-sans-bold text-black dark:text-white ">
                                            Admin
                                        </p>
                                        <SidebarItem
                                            onClick={() => {
                                                navigate('/usage');
                                            }}
                                            current={pathname.startsWith(
                                                '/usage',
                                            )}
                                        >
                                            <ChartBarIcon />
                                            <SidebarLabel>Usage</SidebarLabel>
                                        </SidebarItem>
                                    </SidebarSection>
                                )}

                                <SidebarSection>
                                    <p className="text-lg px-2 plex-sans-bold text-black dark:text-white ">
                                        Help
                                    </p>
                                    <SidebarItem
                                        href="https://support.photoengineers.com/"
                                        target="_blank"
                                    >
                                        <QuestionMarkCircleIcon />
                                        <SidebarLabel>Support</SidebarLabel>
                                    </SidebarItem>
                                    <FeatureFlag
                                        flag={FeatureFlags.PE_ANNOUNCEMENTS}
                                    >
                                        <SidebarItem
                                            onClick={() => {
                                                navigate('/announcements');
                                            }}
                                        >
                                            <MegaphoneIcon />
                                            <SidebarLabel>
                                                Announcements
                                            </SidebarLabel>
                                        </SidebarItem>
                                    </FeatureFlag>
                                    <SidebarItem
                                        onClick={() => {
                                            navigate('/roadmap');
                                        }}
                                    >
                                        <MapIcon />
                                        <SidebarLabel>Roadmap</SidebarLabel>
                                    </SidebarItem>
                                </SidebarSection>
                                <SidebarSection>
                                    <p className="text-lg px-2 plex-sans-bold text-black dark:text-white ">
                                        Apps
                                    </p>
                                    <SidebarItem
                                        href="https://apps.apple.com/us/app/clickerbooth/id6477503550"
                                        target="_blank"
                                    >
                                        <DeviceTabletIcon />
                                        <SidebarLabel>
                                            Clickerbooth
                                        </SidebarLabel>
                                    </SidebarItem>
                                    <SidebarItem
                                        href="https://testflight.apple.com/join/BZBF2mwk"
                                        target="_blank"
                                    >
                                        <DevicePhoneMobileIcon />
                                        <SidebarLabel>Romr Beta</SidebarLabel>
                                    </SidebarItem>
                                    {/*<SidebarItem href="#">
                                        <SparklesIcon />
                                        <SidebarLabel>Changelog</SidebarLabel>
                                    </SidebarItem>*/}
                                </SidebarSection>
                            </SidebarBody>

                            <SidebarFooter className="max-lg:hidden">
                                <Dropdown>
                                    <DropdownButton as={SidebarItem}>
                                        <span className="flex min-w-0 items-center gap-3">
                                            {/*
                                            <Avatar
                                                src="/users/erica.jpg"
                                                className="size-10"
                                                square
                                                alt=""
                                            />*/}
                                            <span className="min-w-0">
                                                <span className="block plex-sans-light truncate text-md font-medium text-zinc-950 dark:text-white">
                                                    {auth?.firstName}{' '}
                                                    {auth?.lastName}
                                                </span>
                                                <span className="block truncate monospace-light text-xs/5 font-normal text-black dark:text-white">
                                                    {auth?.email}
                                                </span>
                                            </span>
                                        </span>
                                        <ChevronUpIcon />
                                    </DropdownButton>
                                    <AccountDropdownMenu anchor="top start" />
                                </Dropdown>
                            </SidebarFooter>
                        </Sidebar>
                    }
                >
                    <FeatureFlag flag={FeatureFlags.PE_ALPHA_PREVIEW}>
                        <div className="flex items-center gap-x-6 bg-[#111827] px-6 py-2.5 sm:px-3.5 sm:before:flex-1 mb-5 rounded-md dark:bg-[#262C2C]">
                            <p
                                className="text-sm/6 text-[#FFFFFF] flex flex-col lg:flex-row items-center"
                                onClick={() => {
                                    navigate('/plans');
                                }}
                            >
                                <strong className="font-semibold plex-sans-bold">
                                    You're enjoying a feature-preview of our
                                    <i> Alpha</i> plan.
                                </strong>
                                <svg
                                    viewBox="0 0 2 2"
                                    aria-hidden="true"
                                    className="mx-2 inline size-0.5 fill-current invisible lg:visible w-0 lg:w-auto"
                                >
                                    <circle r={1} cx={1} cy={1} />
                                </svg>
                                <span>
                                    Learn more about our plans&nbsp;
                                    <span aria-hidden="true">&rarr;</span>
                                </span>
                            </p>
                        </div>
                    </FeatureFlag>
                    <Outlet />
                </SidebarLayout>
                <Toaster position="bottom-left" />
            </LicenseContext.Provider>
        </FeatureFlagContext.Provider>
    );
}

export function ApplicationLayout() {
    const { auth } = useAuth();
    const { orgs } = auth;
    const [org, setOrg] = useLocalStorage('current-org', orgs?.[0]);

    useEffect(() => {
        const doesOrgExistForCurrentUser = orgs?.find(
            (o: any) => o.id === org?.id,
        );

        if (!doesOrgExistForCurrentUser) {
            //console.log('Org does not exist for current user');
            setOrg(orgs?.[0]);
        } else {
            //console.log('Org exists for current user');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orgs]);

    return (
        <OrgContext.Provider value={{ org, setOrg }}>
            <ApplicationLayoutComponent auth={auth} />
        </OrgContext.Provider>
    );
}

export function NoLayout() {
    const { auth } = useAuth();
    const { orgs } = auth;
    const [org, setOrg] = useLocalStorage('current-org', orgs?.[0]);

    useEffect(() => {
        const doesOrgExistForCurrentUser = orgs?.find(
            (o: any) => o.id === org?.id,
        );
        console.log('orgs', orgs);
        console.log('org', org);
        if (!doesOrgExistForCurrentUser) {
            //console.log('Org does not exist for current user');
            setOrg(orgs?.[0]);
        } else {
            //console.log('Org exists for current user');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orgs]);

    return (
        <OrgContext.Provider value={{ org, setOrg }}>
            <NoLayoutComponent />
        </OrgContext.Provider>
    );
}

export function NoLayoutComponent() {
    const { featureFlags } = useFeatureFlags();
    const { license } = useCurrentLicense();

    return (
        <FeatureFlagContext.Provider value={featureFlags}>
            <LicenseContext.Provider value={{ license }}>
                <div className="fixed bottom-4 right-4 ">
                    <img
                        className="w-[140px] clickable cursor-pointer object-contain pe-logo"
                        src={logo}
                        alt="PhotoEngineers"
                    />
                </div>
                <Outlet />
            </LicenseContext.Provider>
        </FeatureFlagContext.Provider>
    );
}
